import {
    booleanAttribute,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    output,
    ChangeDetectionStrategy,
} from "@angular/core";

import { LoaderSpinnerComponent } from "@hermes/aphrodite-loader-spinner";

import { HermesSvg, SvgIconComponent } from "@hermes/aphrodite-svg";

import {
    BUTTON_COLOR,
    BUTTON_DRAWING,
    BUTTON_LAYOUT,
    BUTTON_RADIUS,
    BUTTON_SIZE,
    BUTTON_VARIANT,
    type Draw,
    type Color,
    type Layout,
    type Radius,
    type Size,
    type Variant,
} from "./button.types";

/**
 *
 * W3C component Pattern : [Button Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/button/)
 *
 *
 * #### Usage:
 *
 * ```ts
 *  <button hButton
 *    type="button"
 *    [variant]="BUTTON_VARIANT.FILLED"
 *    [radius]="BUTTON_RADIUS.SMALL"
 *    [color]="BUTTON_COLOR.PRIMARY"
 *    [layout]="BUTTON_LAYOUT.FULL"
 *    [size]="BUTTON_SIZE.LARGE"
 *    [icon]="'minus'"
 *    [iconSize]="['12px', '12px']"
 *    (pressed)="handleClick($event)"
 *  >
 *    button label
 *  </button>
 * ```
 */
@Component({
    selector: "button[hButton], a[hButton]",
    imports: [LoaderSpinnerComponent, SvgIconComponent],
    templateUrl: "./button.component.html",
    styleUrls: ["./button.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "(click)": "onClick($any($event))",
        "[attr.aria-busy]": "loading() || undefined",
        "[attr.aria-haspopup]": "trayTargetId() ? 'dialog' : undefined",
        "[attr.aria-expanded]": "trayTargetId() ? 'false' : undefined",
        "[attr.aria-disabled]": "isDisabled || undefined",
        "[attr.aria-controls]": "trayTargetId()",
        class: "button",
        "[class]": "hostClass()",
        "[class.animated-openning]": "animatedOpenning()",
    },
})
export class ButtonComponent {
    public readonly radius = input<Radius>(BUTTON_RADIUS.SMALL);
    public readonly variant = input<Variant>(BUTTON_VARIANT.FILLED);
    public readonly color = input<Color>(BUTTON_COLOR.PRIMARY);
    public readonly layout = input<Layout>(BUTTON_LAYOUT.FULL);
    public readonly size = input<Size>(BUTTON_SIZE.MEDIUM);
    public readonly icon = input<HermesSvg | undefined>(undefined);
    public readonly iconDrawing = input<Draw | undefined>("stroke");
    public readonly animatedOpenning = input<boolean>(false);

    /**
     * [height, width]
     */
    public readonly iconSize = input<[string, string]>(["12px", "12px"]);
    public readonly loading = input(false, { transform: booleanAttribute });
    public readonly trayTargetId = input<string | undefined>(undefined);
    public readonly pressed = output<MouseEvent>();
    private readonly el = inject(ElementRef<HTMLElement>);
    protected readonly DRAWING = BUTTON_DRAWING;

    public get isDisabled(): boolean {
        return this.el.nativeElement.hasAttribute("disabled");
    }

    public readonly hostClass = computed(() =>
        [
            `button-variant-${this.variant()}`,
            `button-color-${this.color()}`,
            `button-radius-${this.radius()}`,
            `button-size-${this.size()}`,
            `button-layout-${this.layout()}`,
        ].join(" "),
    );

    public onClick(event: MouseEvent) {
        if (this.loading()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        this.pressed.emit(event);
    }
}
